import { Banner, Typography } from "@foodpilot/foods";
import { Box, useTheme } from "@mui/material";
import { useId, type RefObject } from "react";
import { useTranslation } from "react-i18next";

import { getEntityFormStickyTop } from "./EntityFormPage";

export type EntityValidationBannerProps = {
    bannerRef: RefObject<HTMLDivElement | null>;
    entityLabel: string;
    issueCount: number;
};

/** Shared save-time validation summary for long entity forms. */
export const EntityValidationBanner = (props: EntityValidationBannerProps) => {
    const { bannerRef, entityLabel, issueCount } = props;
    const { t } = useTranslation();
    const theme = useTheme();
    const titleId = useId();

    if (issueCount === 0) return null;

    return (
        <Box
            ref={bannerRef}
            role="alert"
            tabIndex={-1}
            aria-labelledby={titleId}
            sx={{
                scrollMarginTop: getEntityFormStickyTop(theme.spacing(2)),
                borderRadius: theme.spacing(1.5),
                "&:focus": { outline: "none" },
                "& > .MuiBox-root": {
                    backgroundColor: theme.custom.red[50],
                    borderColor: theme.custom.red[600],
                    borderWidth: theme.custom.strokes.containerDivider.width,
                    borderStyle: theme.custom.strokes.containerDivider.style,
                    borderRadius: theme.spacing(1.5),
                },
                "& > .MuiBox-root > .MuiStack-root": { alignItems: "flex-start" },
            }}
        >
            <Banner variant="error">
                <Typography
                    id={titleId}
                    component="span"
                    variant="body-semibold"
                    sx={{ display: "block", color: theme.palette.text.primary, marginBottom: theme.spacing(0.5) }}
                >
                    {issueCount === 1 ?
                        t("entity_form.validation.issue", "1 issue needs to be fixed before saving")
                    :   t("entity_form.validation.issues", "{{count}} issues need to be fixed before saving", {
                            count: issueCount,
                        })
                    }
                </Typography>
                <Typography
                    component="span"
                    variant="small-regular"
                    sx={{ display: "block", color: theme.palette.text.secondary }}
                >
                    {t(
                        "entity_form.validation.introduction",
                        "To save this {{entity}}, please correct the following:",
                        { entity: entityLabel },
                    )}
                </Typography>
                <Typography
                    component="span"
                    variant="small-regular"
                    sx={{ display: "block", color: theme.palette.text.secondary, marginTop: theme.spacing(1.5) }}
                >
                    {t("entity_form.validation.required_fields", "• Every field marked with * must be complete.")}
                </Typography>
            </Banner>
        </Box>
    );
};
